import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useApi from '../api';
import useAnalytics from '../analytics/analytics';
import { formatDate } from '../utils/dateFormatter';
import { Separator } from '@/components/ui/separator';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { BadgeCheck, Milestone, Users, UserRoundCheck } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import PageSizeSelect, { DEFAULT_PAGE_SIZE } from "@/components/ui/PageSizeSelect";
import { useTableFilters, DataTableToolbar, useTableSort, SortControl, SortHeader, FilterDef } from "@/components/shared/filters";
import { ACCOUNTS_SORTS, ACCOUNTS_DEFAULT_SORT } from "./accountsFilters";
import AccountFavicon from './AccountFavicon';
import { StageBadge, useLifecycleStages } from './lifecycleStages';
import SalesforceIcon from '../icons/salesforce_logo.png';
import HubspotIcon from '../icons/hubspot_logo.webp';
import ObjectiveProgress, { ObjectiveProgressValue } from '../objectives/ObjectiveProgress';

export interface Account {
  id: string;
  organization_id?: string;
  domain?: string | null;
  external_id?: string | null;
  name?: string | null;
  first_confirmed_at?: string | null;
  crm_match?: {
    status?: string;
    crm?: string;
    account_name?: string;
    account_url?: string;
    owner_email?: string;
    owner_name?: string;
  } | null;
  stage?: string | null;
  stage_source?: string | null;
  stage_updated_at?: string | null;
  lifecycle?: { stage: string; at: string; source: string }[] | null;
  fit_score?: number | null;
  fit_reasoning?: string | null;
  research?: {
    customer_summary?: string;
    company_findings?: string;
    person_findings?: string;
    key_signals?: string[];
    sources?: string[];
    confidence?: string;
    researched_at?: string;
  } | null;
  lead_count: number;
  visitor_count: number;
  last_activity_at?: string | null;
  created_at?: string;
}

export const FitScoreBadge: React.FC<{ score?: number | null }> = ({ score }) => {
  if (score == null) {
    return <span className="text-md text-muted-foreground">—</span>;
  }
  const tone =
    score >= 70
      ? 'text-green-700 dark:text-green-400 bg-green-500/10'
      : score >= 40
        ? 'text-amber-700 dark:text-amber-400 bg-amber-500/10'
        : 'text-muted-foreground bg-muted';
  return (
    <span className={`inline-flex items-center rounded-xl px-2 py-0.5 text-xs font-semibold tabular-nums ${tone}`}>
      {score}
    </span>
  );
};

export interface AccountActivitySummary {
  dau_today: number;
  avg_dau: number;
  mau: number;
  stickiness: number | null;
}

const PAGE_SIZE_STORAGE_KEY = 'accounts:pageSize';

const readStoredPageSize = (): number => {
  if (typeof window === 'undefined') return DEFAULT_PAGE_SIZE;
  const stored = parseInt(window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY) || '', 10);
  return Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_PAGE_SIZE;
};

export const ConfirmedBadge: React.FC<{
  confirmedAt?: string | null;
  externalId?: string | null;
}> = ({ confirmedAt, externalId }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        {confirmedAt ? (
          <span className="inline-flex items-center gap-1 rounded-xl px-2 py-0.5 text-xs font-semibold text-green-700 dark:text-green-400 bg-green-500/10">
            <BadgeCheck className="h-3.5 w-3.5" /> Confirmed
          </span>
        ) : externalId ? (
          <span className="inline-flex items-center gap-1 rounded-xl px-2 py-0.5 text-xs font-semibold text-blue-700 dark:text-blue-400 bg-blue-500/10">
            Identified
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-xl px-2 py-0.5 text-xs font-medium text-muted-foreground bg-muted">
            Enriched
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent>
        {confirmedAt
          ? `A contact with a work email at this domain was first seen ${formatDate(confirmedAt)}`
          : externalId
            ? 'Identified by your app; no matching work-email domain has been confirmed yet'
          : 'Known only from enrichment data — no contact with a work email at this domain yet'}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const AccountsList: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState<number>(readStoredPageSize);
  const navigate = useNavigate();
  const location = useLocation();
  const { get, post } = useApi();
  const { captureEvent } = useAnalytics();
  const [activity, setActivity] = useState<Record<string, AccountActivitySummary>>({});
  const [objectiveProgress, setObjectiveProgress] = useState<Record<string, ObjectiveProgressValue>>({});
  const { stages } = useLifecycleStages();

  const filters: FilterDef[] = React.useMemo(
    () => [
      {
        id: 'stage',
        label: 'Stage',
        icon: <Milestone className="h-3.5 w-3.5" />,
        type: 'select',
        urlParam: 'stage',
        pinned: true,
        searchable: stages.length > 10,
        options: [
          { value: 'all', label: 'All Stages' },
          ...stages.map((s) => ({ value: s.key, label: s.label })),
        ],
      },
    ],
    [stages]
  );

  const {
    values: filterValues,
    setFilter,
    clearFilter,
    clearAll,
    searchValue,
    setSearch,
  } = useTableFilters(filters);
  const {
    activeField: sortField,
    activeOrder: sortOrder,
    isDefault: isDefaultSort,
    setSort,
    toggleField,
    clear: clearSort,
  } = useTableSort(ACCOUNTS_DEFAULT_SORT);

  // URL is the source of truth: every filter / page / page-size change writes
  // the query string, and this effect refetches in response.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page') || '1', 10);
    const perPageParam = parseInt(params.get('per_page') || String(readStoredPageSize()), 10);
    const resolvedPerPage = Number.isFinite(perPageParam) && perPageParam > 0 ? perPageParam : readStoredPageSize();
    setPerPage(resolvedPerPage);
    getAccounts({
      page: Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1,
      perPage: resolvedPerPage,
      search: params.get('search') || '',
      stage: params.get('stage') || '',
      sortBy: params.get('sort_by') || '',
      sortOrder: params.get('sort_order') || '',
    });
  }, [location.search]);

  const getAccounts = async ({
    page = 1,
    perPage: perPageArg = readStoredPageSize(),
    search = '',
    stage = '',
    sortBy = '',
    sortOrder = '',
  }: {
    page?: number;
    perPage?: number;
    search?: string;
    stage?: string;
    sortBy?: string;
    sortOrder?: string;
  } = {}) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(perPageArg),
      });
      if (search) params.set('search', search);
      if (stage) params.set('stage', stage);
      if (sortBy) params.set('sort_by', sortBy);
      if (sortOrder) params.set('sort_order', sortOrder);

      const data = await get(`/accounts/?${params.toString()}`);

      setAccounts(data.data.items);
      setPage(data.data.page);
      setTotalPages(data.data.pages);
      fetchActivity(data.data.items);
      fetchObjectiveProgress(data.data.items);
      captureEvent('viewed_accounts', {});
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
    setLoading(false);
  };

  // Activity is fetched separately so the table renders before the (heavier)
  // live DAU/MAU computation returns.
  const fetchActivity = async (items: Account[]) => {
    if (!items.length) {
      setActivity({});
      return;
    }
    try {
      const response = await post('/accounts/activity/summary', {
        account_ids: items.map((account) => account.id),
        tz_offset_minutes: new Date().getTimezoneOffset(),
      });
      setActivity(response.data.items || {});
    } catch (error) {
      console.error('Error fetching account activity:', error);
    }
  };

  const fetchObjectiveProgress = async (items: Account[]) => {
    if (!items.length) {
      setObjectiveProgress({});
      return;
    }
    try {
      const response = await post('/accounts/objectives/summary', {
        account_ids: items.map((account) => account.id),
      });
      setObjectiveProgress(response.data?.items || {});
    } catch (error) {
      console.error('Error fetching account objective progress:', error);
      setObjectiveProgress({});
    }
  };

  const goToPage = (nextPage: number) => {
    const params = new URLSearchParams(location.search);
    params.set('page', String(nextPage));
    navigate(`?${params.toString()}`);
  };

  const handlePerPageChange = (value: number) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(value));
    }
    const params = new URLSearchParams(location.search);
    params.set('per_page', String(value));
    params.set('page', '1');
    navigate(`?${params.toString()}`);
  };

  const renderSortHeader = (label: string, field: string) => (
    <SortHeader
      label={label}
      field={field}
      activeField={sortField}
      activeOrder={sortOrder}
      onSort={(f) => toggleField(f, ACCOUNTS_SORTS.find((s) => s.field === f)?.defaultOrder)}
    />
  );

  const columns = React.useMemo(() => [
    {
      accessorKey: 'name',
      size: 220,
      header: () => renderSortHeader('Name', 'name'),
      cell: ({ row }: any) => (
        <span className="text-md font-semibold flex items-center gap-2 truncate" title={row.original.name || row.original.domain || row.original.external_id}>
          <AccountFavicon domain={row.original.domain} />
          <span className="truncate">{row.original.name || row.original.domain || row.original.external_id}</span>
        </span>
      ),
    },
    {
      accessorKey: 'domain',
      size: 180,
      header: () => renderSortHeader('Domain', 'domain'),
      cell: ({ getValue }: any) => (
        <span className="text-md text-muted-foreground block truncate" title={getValue()}>
          {getValue() || '-'}
        </span>
      ),
    },
    {
      accessorKey: 'external_id',
      size: 150,
      header: 'External ID',
      cell: ({ getValue }: any) => (
        <span
          className="text-sm font-mono text-muted-foreground block truncate"
          title={getValue() || undefined}
        >
          {getValue() || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'first_confirmed_at',
      size: 130,
      header: () => renderSortHeader('Status', 'first_confirmed_at'),
      cell: ({ getValue, row }: any) => (
        <ConfirmedBadge confirmedAt={getValue()} externalId={row.original.external_id} />
      ),
    },
    {
      accessorKey: 'stage',
      size: 140,
      header: () => renderSortHeader('Stage', 'stage'),
      cell: ({ getValue }: any) => <StageBadge stageKey={getValue()} stages={stages} />,
    },
    {
      id: 'objectives',
      size: 130,
      header: 'Objectives',
      cell: ({ row }: any) => <ObjectiveProgress progress={objectiveProgress[row.original.id]} />,
    },
    {
      id: 'crm',
      size: 70,
      header: 'CRM',
      cell: ({ row }: any) => {
        const match = row.original.crm_match;
        if (!match || match.status !== 'matched') {
          return <span className="text-md text-muted-foreground">-</span>;
        }
        const icon = match.crm === 'salesforce' ? SalesforceIcon : match.crm === 'hubspot' ? HubspotIcon : null;
        const name = match.crm === 'salesforce' ? 'Salesforce' : match.crm === 'hubspot' ? 'HubSpot' : 'CRM';
        if (!icon) {
          return <span className="text-md text-muted-foreground">-</span>;
        }
        const label = match.account_name
          ? `View ${match.account_name} in ${name}`
          : `View account in ${name}`;
        const image = (
          <img
            src={icon}
            alt={name}
            className="h-5 w-5 object-contain transition-opacity hover:opacity-75"
          />
        );
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                {match.account_url ? (
                  <a
                    href={match.account_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={(event) => event.stopPropagation()}
                    aria-label={label}
                  >
                    {image}
                  </a>
                ) : (
                  <span className="inline-flex">{image}</span>
                )}
              </TooltipTrigger>
              <TooltipContent>{label}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      accessorKey: 'fit_score',
      size: 90,
      header: () => renderSortHeader('Fit', 'fit_score'),
      cell: ({ getValue, row }: any) => (
        <span title={row.original.fit_reasoning || undefined}>
          <FitScoreBadge score={getValue()} />
        </span>
      ),
    },
    {
      accessorKey: 'lead_count',
      size: 110,
      header: () => renderSortHeader('Contacts', 'lead_count'),
      cell: ({ getValue }: any) => (
        <span className="inline-flex items-center gap-1.5 text-md">
          <UserRoundCheck className="h-3.5 w-3.5 opacity-60" /> {getValue() ?? 0}
        </span>
      ),
    },
    {
      accessorKey: 'visitor_count',
      size: 110,
      header: () => renderSortHeader('Visitors', 'visitor_count'),
      cell: ({ getValue }: any) => (
        <span className="inline-flex items-center gap-1.5 text-md">
          <Users className="h-3.5 w-3.5 opacity-60" /> {getValue() ?? 0}
        </span>
      ),
    },
    {
      accessorKey: 'dau',
      size: 90,
      header: () => (
        <span title="People active today — an Aimdoc conversation or in-app activity">DAU</span>
      ),
      cell: ({ row }: any) => {
        const summary = activity[row.original.id];
        if (!summary) return <span className="text-md text-muted-foreground">…</span>;
        if (!summary.mau) return <span className="text-md text-muted-foreground">—</span>;
        return (
          <span
            className="text-md tabular-nums"
            title={`Today: ${summary.dau_today} · 30-day average: ${summary.avg_dau}`}
          >
            {summary.dau_today}
          </span>
        );
      },
    },
    {
      accessorKey: 'stickiness',
      size: 110,
      header: () => (
        <span title="Stickiness: average DAU ÷ MAU over the last 30 days">DAU/MAU</span>
      ),
      cell: ({ row }: any) => {
        const summary = activity[row.original.id];
        if (!summary) return <span className="text-md text-muted-foreground">…</span>;
        if (summary.stickiness == null) return <span className="text-md text-muted-foreground">—</span>;
        return (
          <span className="text-md tabular-nums" title={`MAU: ${summary.mau}`}>
            {Math.round(summary.stickiness * 100)}%
          </span>
        );
      },
    },
    {
      accessorKey: 'last_activity_at',
      size: 150,
      header: () => renderSortHeader('Last Activity', 'last_activity_at'),
      cell: ({ getValue }: any) => (
        <span className="text-md">{getValue() ? formatDate(getValue()) : '-'}</span>
      ),
    },
  ], [sortField, sortOrder, toggleField, activity, stages, objectiveProgress]);

  const table = useReactTable({
    data: accounts,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const AccountsLoadingSkeleton = () => (
    <TableBody>
      {[1, 2].map((i) => (
        <TableRow key={i} className="animate-pulse">
          {columns.map((_, j) => (
            <TableCell key={j} className="px-6 py-4">
              <div className="h-4 bg-muted rounded w-3/4 text-md" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );

  return (
    <div className="p-12 overflow-y-auto max-h-[calc(100vh-1px)] scrollbar-thin grow">
      <h1 className='text-4xl font-bold flex items-center gap-2'>Accounts</h1>
      <p className='mt-4 text-sm text-slate-500'>Companies observed across your contacts and website visitors.</p>
      <Separator className="my-4" />
      <DataTableToolbar
        className="mt-2"
        filters={filters}
        values={filterValues}
        onChange={setFilter}
        onClear={clearFilter}
        onClearAll={clearAll}
        search={{ value: searchValue, onChange: setSearch, placeholder: 'Search by name, domain, or external ID...' }}
        rightSlot={
          <SortControl
            sorts={ACCOUNTS_SORTS}
            activeField={sortField}
            activeOrder={sortOrder}
            isDefault={isDefaultSort}
            onSelect={setSort}
            onClear={clearSort}
          />
        }
      />
      <div className="flex flex-col mt-4">
        <div className="-m-1.5 overflow-x-auto scrollbar-hidden">
          <div className="p-1.5 w-full align-middle">
            <div className="border rounded-lg shadow overflow-hidden">
              <Table className="table-fixed w-full">
                <TableHeader>
                  {table.getHeaderGroups().map(headerGroup => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <TableHead key={header.id} style={{ width: header.getSize() }} className="truncate">
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                {loading ? (
                  <AccountsLoadingSkeleton />
                ) : (
                  <TableBody>
                    {table.getRowModel().rows.length ? (
                      table.getRowModel().rows.map(row => (
                        <TableRow
                          key={row.id}
                          className="text-md cursor-pointer"
                          onClick={() => navigate(`/accounts/${row.original.id}`)}
                        >
                          {row.getVisibleCells().map(cell => (
                            <TableCell key={cell.id} style={{ width: cell.column.getSize() }} className="overflow-hidden">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={columns.length} className="h-24 text-center">
                          No accounts yet — accounts appear automatically when a contact with a work email or an identified company visits.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                )}
              </Table>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <PageSizeSelect value={perPage} onChange={handlePerPageChange} disabled={loading} />
          {totalPages > 1 && (
            <div className='flex flex-row items-center gap-4'>
              <Button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1 || loading}
                variant="outline"
              >
                Previous
              </Button>
              <span className="text-sm font-semibold">
                Page {page} of {totalPages}
              </span>
              <Button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages || loading}
                variant="outline"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountsList;
